import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HoaReturn } from '../clients/rust-api-client';
import { hoaService } from '../clients/ApiClients';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  getCompanyIdFromToken,
  getUserTypeFromToken,
} from '../utils/TokenUtils';
import { canCreateHoa, UserType } from '../constants/UserTypes';
import { useSyncedHoas } from '../sync/hooks';
import { useCommandPalette } from '../shell/CommandPaletteProvider';
import { CreateButton } from '../shell/CreateButton';
import {
  DirectoryColumn,
  DirectoryList,
  EMPTY_CELL,
  StatusCell,
} from '../shell/DirectoryList';
import { FilterBar } from '../violation/linear/FilterBar';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { apiErrorMessage } from '../utils/apiErrors';
import { formatPhoneNumber } from '../utils/phoneUtils';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const hoaStatus = (hoa: HoaReturn) => (hoa.is_active ? 'active' : 'inactive');

const locality = (hoa: HoaReturn) =>
  [hoa.city, hoa.state].filter(Boolean).join(', ');

const COLUMNS: DirectoryColumn<HoaReturn>[] = [
  {
    key: 'location',
    label: 'Location',
    className: 'w-44 hidden sm:block',
    render: (hoa) => locality(hoa) || EMPTY_CELL,
    sortValue: locality,
  },
  {
    key: 'email',
    label: 'Contact',
    className: 'w-56 hidden lg:block',
    render: (hoa) => hoa.contact_email || EMPTY_CELL,
    sortValue: (hoa) => hoa.contact_email ?? '',
  },
  {
    key: 'phone',
    label: 'Phone',
    className: 'w-32 hidden xl:block',
    render: (hoa) =>
      hoa.contact_phone ? formatPhoneNumber(hoa.contact_phone) : EMPTY_CELL,
  },
  {
    key: 'status',
    label: 'Status',
    className: 'w-24',
    render: (hoa) => (
      <StatusCell active={hoa.is_active} label={hoa.is_active ? 'Active' : 'Inactive'} />
    ),
    sortValue: hoaStatus,
  },
];

const HoaListComponent: React.FC = () => {
  const navigate = useNavigate();
  const [fetchedHoas, setHoas] = useState<HoaReturn[]>([]);
  // The sync engine's local copy when it has one (#561); fetched otherwise.
  const syncedHoas = useSyncedHoas();
  const hoas = syncedHoas ?? fetchedHoas;
  const haveSynced = syncedHoas !== null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statuses, setStatuses] = useState<string[]>([]);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const companyId = useMemo(() => getCompanyIdFromToken(), []);
  const userType = useMemo(() => getUserTypeFromToken(), []);
  const { openNewHoa } = useCommandPalette();

  useEffect(() => {
    const fetchData = async () => {
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        // SuperAdmin can view all HOAs, others can only view their company's HOAs
        const response =
          userType === UserType.SUPER_ADMIN
            ? await hoaService.getAllHoas()
            : await hoaService.getHoasByCompany({ companyId: companyId || '' });
        setHoas(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(apiErrorMessage(err, "Couldn't load HOAs."));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userType, companyId, haveSynced]);

  const filteredHoas = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return hoas.filter((hoa) => {
      if (statuses.length && !statuses.includes(hoaStatus(hoa))) return false;
      if (!searchTerm) return true;

      const addressString = [
        hoa.street,
        hoa.street2,
        hoa.city,
        hoa.state,
        hoa.zip,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return (
        hoa.name.toLowerCase().includes(searchLower) ||
        addressString.includes(searchLower) ||
        (hoa.contact_email &&
          hoa.contact_email.toLowerCase().includes(searchLower)) ||
        (hoa.contact_phone && hoa.contact_phone.includes(searchTerm))
      );
    });
  }, [hoas, searchTerm, statuses]);

  // The shared create modal (#665), mounted in CommandPaletteProvider.
  // Created from here, the new association joins this list instead of
  // being opened; the sync copy, when there is one, adds it on its own.
  const handleAddHoa = () =>
    openNewHoa({
      onCreated: (created) => {
        setHoas((prevHoas) => [...prevHoas, created]);
        setError(null);
        setShowSuccessAlert(true);
      },
    });

  // With a synced copy there is nothing to wait for: render it on the
  // first paint, not after the effect clears `loading` (#635).
  if (loading && !haveSynced) {
    return <LoadingSpinner />;
  }

  const filtered = searchTerm !== '' || statuses.length > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Flush against the panel's edges, alerts need their own inset. */}
      <div className="space-y-2 px-3 pt-3 empty:hidden">
        {error && (
          <ErrorAlert title="Couldn't load HOAs" onDismiss={() => setError(null)}>
            {error}
          </ErrorAlert>
        )}
        {showSuccessAlert && (
          <ShowAlert
            alertType="success"
            alertTitle="Success"
            alertMessage="HOA created successfully!"
            isVisible={showSuccessAlert}
            onClose={() => setShowSuccessAlert(false)}
            timeout={5000}
            isInline={true}
          />
        )}
      </div>

      <FilterBar
        fields={[
          {
            field: 'Status',
            options: STATUS_OPTIONS,
            values: statuses,
            onChange: setStatuses,
          },
        ]}
        search={searchTerm}
        onSearch={setSearchTerm}
        searchLabel="Search HOAs by name, address or contact"
        onClearAll={() => setStatuses([])}
        shown={filteredHoas.length}
        total={hoas.length}
        trailing={
          canCreateHoa(userType) && <CreateButton label="New HOA" onClick={handleAddHoa} />
        }
      />
      <DirectoryList
        items={filteredHoas}
        getKey={(hoa) => hoa.id}
        getName={(hoa) => hoa.name}
        isMuted={(hoa) => !hoa.is_active}
        columns={COLUMNS}
        onOpen={(hoa) => navigate(`/hoa/${hoa.id}`)}
        emptyMessage={filtered ? 'No HOAs match these filters.' : 'No HOAs yet.'}
      />
    </div>
  );
};

export default HoaListComponent;
